import { useState } from 'react';
import { CalendarClock, MapPin, Timer, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { HrColumnChart } from '../components/ColumnChart';
import { HrExportButtons } from '../components/ExportButtons';
import { HrHeatmap } from '../components/Heatmap';
import { HrKpiGrid } from '../components/KpiGrid';
import {
  countBy,
  downloadCsv,
  downloadXlsx,
  exportFilename,
  median,
  percent,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrSectionProps } from './types';

const AGE_BANDS: { label: string; min: number; max: number }[] = [
  { label: 'Under 25', min: 0, max: 25 },
  { label: '25–34', min: 25, max: 35 },
  { label: '35–44', min: 35, max: 45 },
  { label: '45–54', min: 45, max: 55 },
  { label: '55+', min: 55, max: 200 },
];

const TENURE_BANDS: { label: string; min: number; max: number }[] = [
  { label: '<1 yr', min: 0, max: 1 },
  { label: '1–2', min: 1, max: 2 },
  { label: '2–3', min: 2, max: 3 },
  { label: '3–5', min: 3, max: 5 },
  { label: '5+', min: 5, max: 999 },
];

export function DemographicsSection({
  people,
  filters,
  asOf,
  canExport,
  checkExport,
}: HrSectionProps) {
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);

  const withAge = people.filter((person) => person.age !== null);
  const withTenure = people.filter((person) => person.tenure !== null);

  const averageAge =
    withAge.length > 0
      ? withAge.reduce((total, person) => total + (person.age as number), 0) / withAge.length
      : null;
  const medianTenure = median(withTenure.map((person) => person.tenure as number));

  const ageData = AGE_BANDS.map((band) => ({
    label: band.label,
    full: band.label,
    value: withAge.filter(
      (person) =>
        (person.age as number) >= band.min && (person.age as number) < band.max
    ).length,
  }));

  const tenureData = TENURE_BANDS.map((band) => ({
    label: band.label,
    full: band.label,
    value: withTenure.filter(
      (person) =>
        (person.tenure as number) >= band.min && (person.tenure as number) < band.max
    ).length,
  }));

  const departments = sortCountsDesc(countBy(people, (person) => person.dept)).map(
    ([label]) => label
  );

  const heatmapRows = departments.map((department) => {
    const departmentPeople = people.filter(
      (person) => (person.dept || 'Not recorded') === department
    );
    return {
      key: department,
      label: department,
      values: TENURE_BANDS.map((band) => {
        return departmentPeople.filter(
          (person) =>
            person.tenure !== null &&
            person.tenure >= band.min &&
            person.tenure < band.max
        ).length;
      }),
      unknown: departmentPeople.filter((person) => person.tenure === null).length,
    };
  });

  const regionCounts = countBy(people, (person) => person.region);
  const regions = ['NSW', 'QLD', 'Unknown'].map((label) => ({
    label,
    value: regionCounts.get(label) || 0,
    color:
      label === 'NSW'
        ? 'var(--chart-3)'
        : label === 'QLD'
          ? 'var(--chart-2)'
          : 'var(--muted-foreground)',
  }));

  const retirementRisk = withAge.filter((person) => (person.age as number) >= 55).length;

  const handleExport = async (format: 'csv' | 'xlsx') => {
    if (!canExport || !(await checkExport())) return;
    setExporting(format);
    try {
      const headers = ['Department', ...TENURE_BANDS.map((band) => band.label), 'Unknown'];
      const rows = heatmapRows.map((row) => [row.label, ...row.values, row.unknown || 0]);
      const filename = exportFilename(
        'Tenure by department',
        filters,
        asOf,
        format === 'csv' ? 'csv' : 'xlsx'
      );
      if (format === 'csv') downloadCsv(filename, headers, rows);
      else downloadXlsx(filename, 'Tenure by department', headers, rows);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Average age',
            value: averageAge === null ? '—' : averageAge.toFixed(0),
            unit: 'yrs',
            detail: `Based on ${withAge.length} of ${people.length} with DOB`,
            tone: percent(withAge.length, people.length) < 50 ? 'warning' : 'primary',
            icon: <Users className="h-5 w-5" />,
          },
          {
            label: 'Median tenure',
            value: medianTenure === null ? '—' : medianTenure.toFixed(1),
            unit: 'yrs',
            detail: `${withTenure.length} with a start date`,
            icon: <Timer className="h-5 w-5" />,
          },
          {
            label: 'Under 1 year service',
            value: withTenure.filter((person) => (person.tenure as number) < 1).length,
            detail: `${percent(
              withTenure.filter((person) => (person.tenure as number) < 1).length,
              withTenure.length
            )}% of known`,
            icon: <CalendarClock className="h-5 w-5" />,
          },
          {
            label: '5+ years service',
            value: withTenure.filter((person) => (person.tenure as number) >= 5).length,
            icon: <CalendarClock className="h-5 w-5" />,
          },
          {
            label: 'QLD-based',
            value: regionCounts.get('QLD') || 0,
            detail: 'Work Location, or inferred from department/address',
            icon: <MapPin className="h-5 w-5" />,
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Age profile</CardTitle>
            <CardDescription>
              {withAge.length} people with date of birth · {people.length - withAge.length} missing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HrColumnChart data={ageData} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Length of service</CardTitle>
            <CardDescription>{withTenure.length} people with a start date</CardDescription>
          </CardHeader>
          <CardContent>
            <HrColumnChart data={tenureData} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Tenure by department</CardTitle>
              <CardDescription>Where experience sits · darker cells hold more people</CardDescription>
            </div>
            {canExport ? (
              <HrExportButtons onExport={handleExport} disabled={exporting !== null} exporting={exporting} />
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <HrHeatmap
            columns={TENURE_BANDS.map((band) => band.label)}
            rows={heatmapRows}
            unknownLabel="No start date"
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Location</CardTitle>
            <CardDescription>
              Work Location where recorded; otherwise inferred from department or address
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex h-5 overflow-hidden rounded-md bg-muted">
              {regions.map((region) =>
                region.value > 0 ? (
                  <div
                    key={region.label}
                    title={`${region.label}: ${region.value}`}
                    style={{
                      width: `${percent(region.value, people.length)}%`,
                      backgroundColor: region.color,
                    }}
                  />
                ) : null
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {regions.map((region) => (
                <span key={region.label} className="inline-flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: region.color }}
                  />
                  <span className="text-muted-foreground">{region.label}</span>
                  <strong className="tabular-nums text-foreground">{region.value}</strong>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Data notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <strong className="font-medium text-foreground">Retirement-risk watch:</strong>{' '}
              {retirementRisk} people aged 55 or over on record.
            </p>
            <p>
              <strong className="font-medium text-foreground">Data gap:</strong>{' '}
              {people.length - withAge.length} people have no date of birth, so the age profile is
              indicative only.
            </p>
            <p>Date of birth is never returned by the API; only the whole-year age is used.</p>
            <p>
              Region uses the structured Work Location field where it is recorded, and only
              falls back to department/address inference otherwise.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
